<template>
  <!-- Container for the login page with enhanced gradient background -->
  <div
    class="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gradient-to-br from-indigo-100 via-purple-50 to-blue-50 relative overflow-hidden">
    <!-- Enhanced decorative background elements -->
    <div
      class="absolute top-20 left-10 w-32 h-32 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob">
    </div>
    <div
      class="absolute top-40 right-10 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000">
    </div>
    <div
      class="absolute bottom-20 left-1/3 w-32 h-32 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000">
    </div>
    <!-- Additional decorative elements -->
    <div
      class="absolute top-1/4 right-1/3 w-24 h-24 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-blob animation-delay-3000">
    </div>
    <div
      class="absolute bottom-1/3 right-1/4 w-36 h-36 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-blob animation-delay-5000">
    </div>
    <div
      class="absolute top-3/4 left-1/4 w-28 h-28 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-blob-alt">
    </div>

    <!-- Subtle pattern overlay -->
    <div class="absolute inset-0 bg-grid-pattern opacity-10"></div>

    <!-- Floating particles -->
    <div class="particles">
      <div class="particle particle-1"></div>
      <div class="particle particle-2"></div>
      <div class="particle particle-3"></div>
      <div class="particle particle-4"></div>
      <div class="particle particle-5"></div>
    </div>

    <!-- Logo and heading -->
    <div class="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
      <img class="mx-auto h-10 w-auto" src="~assets/images/Logo_Temp.png" alt="Livia" />
      <h2 class="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
        Sign in to your account
      </h2>
    </div>

    <!-- Login form container -->
    <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
      <div class="bg-white px-6 py-6 shadow-lg sm:rounded-lg sm:px-12 backdrop-blur-sm bg-white/95">
        <!-- Login Tabs -->
        <div class="mb-6 border-b border-gray-200">
          <div class="flex -mb-px">
            <button @click="activeTab = 'email'"
              class="py-2 px-4 text-center border-b-2 font-medium text-sm focus:outline-none transition-colors duration-200 ease-in-out"
              :class="activeTab === 'email'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                ">
              Email & Password
            </button>
            <button @click="activeTab = 'phone'"
              class="ml-8 py-2 px-4 text-center border-b-2 font-medium text-sm focus:outline-none transition-colors duration-200 ease-in-out"
              :class="activeTab === 'phone'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                ">
              Phone Number
            </button>
          </div>
        </div>

        <!-- Email Login Form -->
        <div v-if="activeTab === 'email'">
          <form class="space-y-6" @submit.prevent="signIn">
            <div>
              <label for="email" class="block text-sm font-medium leading-6 text-gray-900">
                Email address
              </label>
              <div class="mt-2">
                <input id="email" v-model="email" name="email" type="email" autocomplete="email" required
                  class="block w-full rounded-md border-0 py-1.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
              </div>
            </div>

            <div>
              <label for="password" class="block text-sm font-medium leading-6 text-gray-900">
                Password
              </label>
              <div class="mt-2">
                <input id="password" v-model="password" name="password" type="password" autocomplete="current-password"
                  required
                  class="block w-full rounded-md border-0 py-1.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <input id="remember-me" name="remember-me" type="checkbox"
                  class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                <label for="remember-me" class="ml-3 block text-sm leading-6 text-gray-900">
                  Remember me
                </label>
              </div>

              <div class="text-sm leading-6">
                <NuxtLink to="/forgot-password" class="font-semibold text-indigo-600 hover:text-indigo-500">
                  Forgot password?
                </NuxtLink>
              </div>
            </div>

            <div>
              <button type="submit"
                class="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Sign in
              </button>
            </div>
          </form>

          <!-- Social login options -->
          <div>
            <div class="relative mt-10">
              <div class="absolute inset-0 flex items-center" aria-hidden="true">
                <div class="w-full border-t border-gray-200" />
              </div>
              <div class="relative flex justify-center text-sm font-medium leading-6">
                <span class="bg-white px-6 text-gray-900">Or continue with</span>
              </div>
            </div>

            <div class="mt-6">
              <!-- Google sign-in button -->
              <a href="#" @click.prevent="signInWithGoogle"
                class="flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:ring-transparent transition-all duration-300 hover:shadow-md">
                <svg class="h-5 w-5" aria-hidden="true" viewBox="0 0 24 24">
                  <path
                    d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                    fill="#EA4335" />
                  <path
                    d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                    fill="#4285F4" />
                  <path
                    d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                    fill="#FBBC05" />
                  <path
                    d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
                    fill="#34A853" />
                </svg>
                <span class="text-sm font-semibold leading-6">Google</span>
              </a>
            </div>
          </div>
        </div>

        <!-- Phone Login Form -->
        <div v-if="activeTab === 'phone'" class="space-y-6">
          <!-- Phone Number Input (Step 1) -->
          <div v-if="phoneAuthStep === 1">
            <div>
              <label for="phoneNumber" class="block text-sm font-medium leading-6 text-gray-900">
                Phone Number
              </label>
              <div class="mt-2">
                <input id="phoneNumber" v-model="phoneNumber" name="phoneNumber" type="tel" placeholder="(123) 456-7890"
                  required
                  class="block w-full rounded-md border-0 py-1.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
              </div>
              <p v-if="phoneError" class="mt-2 text-sm text-red-600">
                {{ phoneError }}
              </p>
            </div>

            <div class="mt-6">
              <button @click="startPhoneVerification"
                class="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Send Verification Code
              </button>
            </div>
          </div>

          <!-- Verification Code Input (Step 2) -->
          <div v-if="phoneAuthStep === 2" class="space-y-6">
            <div class="text-center mb-4">
              <p class="text-sm text-gray-600">
                We've sent a verification code to
                {{ formatPhoneNumberForDisplay(phoneNumber) }}
              </p>
            </div>

            <div>
              <label for="verificationCode" class="block text-sm font-medium leading-6 text-gray-900">
                Verification Code
              </label>
              <div class="mt-2">
                <input id="verificationCode" v-model="verificationCode" type="text" placeholder="Enter 6-digit code"
                  maxlength="6" required
                  class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 text-center tracking-widest" />
              </div>
              <p v-if="verificationError" class="mt-2 text-sm text-red-600">
                {{ verificationError }}
              </p>
            </div>

            <div class="flex flex-col space-y-3">
              <button @click="verifyPhoneNumber"
                class="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Verify and Sign In
              </button>
              <button @click="resendVerificationCode"
                class="text-indigo-600 hover:text-indigo-500 text-sm font-semibold">
                Resend Code
              </button>
              <button @click="phoneAuthStep = 1" class="text-gray-600 hover:text-gray-500 text-sm">
                Change Phone Number
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Registration link -->
      <p class="mt-10 text-center text-sm text-gray-600 backdrop-blur-sm py-2 rounded-full bg-white/30">
        Not a member?
        <NuxtLink to="/onboarding" class="font-semibold leading-6 text-indigo-600 hover:text-indigo-500">
          Register Now
        </NuxtLink>
      </p>
    </div>
  </div>

  <!-- Loading Overlay -->
  <div v-if="loading"
    class="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
    <div class="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full">
      <div class="flex flex-col items-center">
        <div class="relative w-20 h-20 mb-6">
          <div
            class="absolute top-0 left-0 w-full h-full border-4 border-indigo-200 rounded-full animate-spin border-t-indigo-600">
          </div>
        </div>
        <p class="text-gray-700 font-medium text-lg">{{ loadingMessage }}</p>
      </div>
    </div>
  </div>

  <!-- Snackbar for messages -->
  <div v-if="snackbar.show"
    class="fixed bottom-6 right-6 z-50 p-4 rounded-xl shadow-xl transition-all duration-300 max-w-md" :class="{
      'bg-gradient-to-r from-red-500 to-red-600': snackbar.type === 'error',
      'bg-gradient-to-r from-green-500 to-green-600':
        snackbar.type === 'success',
      'bg-gradient-to-r from-blue-500 to-blue-600': snackbar.type === 'info',
      'translate-y-0 opacity-100': snackbar.show,
      'translate-y-full opacity-0': !snackbar.show,
    }">
    <div class="flex items-center">
      <span class="text-white font-medium">{{ snackbar.message }}</span>
      <button @click="closeSnackbar" class="ml-4 text-white hover:text-gray-200 focus:outline-none">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  </div>

  <!-- Invisible reCAPTCHA container -->
  <div id="recaptcha-container"></div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRouter } from 'vue-router'
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  deleteUser,
  signOut,
} from "firebase/auth"
import { useAuthStore } from "~/stores/AuthStore"
import { usePersonaStore } from "~/stores/PersonaStore"
import { doc, getDoc } from 'firebase/firestore'

const auth = useFirebaseAuth()

// Tab management
const activeTab = ref("email")

// Email login
const email = ref("")
const password = ref("")

// Phone login
const phoneAuthStep = ref(1)
const phoneNumber = ref("")
const verificationCode = ref("")
const phoneError = ref("")
const verificationError = ref("")
const confirmationResult = ref(null)
const recaptchaVerifier = ref(null)

// UI state
const loading = ref(false)
const loadingMessage = ref("Processing your request...")
const snackbar = ref({
  show: false,
  message: "",
  type: "error",
  duration: 5000,
  timeout: null,
})

// Computed
const isPhoneNumberValid = computed(() => {
  const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/
  return phoneRegex.test(phoneNumber.value)
})

// Router and stores
const router = useRouter()
const authStore = useAuthStore()
const personaStore = usePersonaStore()
// Methods
const setupRecaptcha = () => {
  if (process.client) {
    try {
      const recaptchaContainer = document.getElementById("recaptcha-container")
      if (recaptchaContainer.innerHTML !== "") {
        recaptchaContainer.innerHTML = ""
      }

      recaptchaVerifier.value = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => {
            console.log("reCAPTCHA verified")
          },
          "expired-callback": () => {
            showSnackbar({
              message: "reCAPTCHA expired. Please try again.",
              type: "error",
            })
            setupRecaptcha()
          },
        }
      )
    } catch (error) {
      console.error("Error initializing reCAPTCHA:", error)
    }
  }
}

const signIn = async () => {
  loading.value = true
  loadingMessage.value = "Signing in..."

  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email.value,
      password.value
    )
    const user = userCredential.user

    // Check if email is verified
    if (!user.emailVerified) {
      // Only set minimal auth state for unverified users
      authStore.SetUserDetails(user)
      router.push("/verify-email")
      return
    }

    // Email is verified, set up full user state
    authStore.SetUserDetails(user)
    localStorage.setItem("user", JSON.stringify(user))
    router.push("/property-search")
  } catch (error) {
    if (error.code === "auth/invalid-credential") {
      showSnackbar({
        message: "Invalid credentials. Please try again.",
        type: "error",
      })
    } else {
      showSnackbar({
        message: "Error signing in: " + error.message,
        type: "error",
      })
    }
  } finally {
    loading.value = false
  }
}

/**
 * This is a fix to prevent firebase creating loose profiles through google login.
 */
const checkProfile = async (userId) => {
  console.log("Checking profile for user: ", userId)
  const db = useFirestore()
  try {
    const userDoc = await getDoc(doc(db, "personadata", userId));
    console.log("Profile exists: ", userDoc.exists())
    return userDoc.exists()
  } catch (error) {
    console.error("Error checking profile: ", error)
    return false
  }
}

const signInWithGoogle = async () => {
  loading.value = true
  loadingMessage.value = "Signing in with Google..."

  const provider = new GoogleAuthProvider()
  try {
    const userCredential = await signInWithPopup(auth, provider)
    const user = userCredential.user
    const userExists = await checkProfile(user.uid)
    if(!userExists){
      await deleteUser(user);  // This WILL work right after login
      await signOut(auth);

      console.warn("Google profile doesn't exist.")

      showSnackbar({
        message: "Please register first before using Google sign-in",
        type: "info",
      })
      
      return;
    }

    if (user) {
      authStore.SetUserDetails(user)
      localStorage.setItem("user", JSON.stringify(user))
      router.push("/property-search")
    }
  } catch (error) {

    if (error.code === "auth/invalid-credential") {
      showSnackbar({
        message: "Invalid Google credentials. Please try again.",
        type: "error",
      })
    } else {
      showSnackbar({
        message: "Error signing in with Google: " + error.message,
        type: "error",
      })
    }
  } finally {
    loading.value = false
  }
}

const startPhoneVerification = async () => {
  if (!validatePhoneNumber()) {
    return
  }

  loading.value = true
  loadingMessage.value = "Checking registration..."
  phoneError.value = ""

  try {
    const formattedNumber = formatPhoneNumber(phoneNumber.value)

    const response = await $fetch("/api/auth/check-phone", {
      method: "POST",
      body: { phoneNumber: formattedNumber },
    })

    if (response.isRegistered) {
      loadingMessage.value = "Sending verification code..."

      if (!recaptchaVerifier.value) {
        setupRecaptcha()
        await new Promise((resolve) => setTimeout(resolve, 500))
      }

      if (!recaptchaVerifier.value) {
        console.error("reCAPTCHA verifier not initialized correctly.")
        throw new Error("reCAPTCHA setup failed. Please refresh and try again.")
      }

      confirmationResult.value = await signInWithPhoneNumber(
        auth,
        formattedNumber,
        recaptchaVerifier.value
      )

      phoneAuthStep.value = 2
      showSnackbar({
        message: "Verification code sent!",
        type: "success",
      })
    } else {
      alert("Phone number not registered. Please complete registration.")
      showSnackbar({
        message: "Phone number not registered. Please complete registration.",
        type: "info",
      })
      router.push("/onboarding")
    }
  } catch (error) {
    console.error("Error during phone verification process:", error)
    let userMessage = "An error occurred. Please try again."
    if (error.data && error.data.message) {
      userMessage = error.data.message
    } else if (error.message) {
      userMessage = error.message
    }
    phoneError.value = userMessage
    showSnackbar({
      message: `Error: ${userMessage}`,
      type: "error",
    })

    try {
      if (
        window.recaptchaVerifier &&
        typeof window.recaptchaVerifier.reset === "function"
      ) {
        window.recaptchaVerifier.reset()
      } else if (
        recaptchaVerifier.value &&
        typeof recaptchaVerifier.value.reset === "function"
      ) {
        recaptchaVerifier.value.reset()
      } else {
        setupRecaptcha()
      }
    } catch (resetError) {
      console.error("Error resetting reCAPTCHA:", resetError)
      setupRecaptcha()
    }
  } finally {
    loading.value = false
  }
}

const resendVerificationCode = async () => {
  loading.value = true
  loadingMessage.value = "Resending verification code..."

  try {
    setupRecaptcha()
    const formattedNumber = formatPhoneNumber(phoneNumber.value)

    confirmationResult.value = await signInWithPhoneNumber(
      auth,
      formattedNumber,
      recaptchaVerifier.value
    )

    showSnackbar({
      message: "Verification code resent!",
      type: "success",
    })
  } catch (error) {
    console.error("Error resending verification code:", error)
    showSnackbar({
      message: "Error resending code: " + error.message,
      type: "error",
    })
  } finally {
    loading.value = false
  }
}

const verifyPhoneNumber = async () => {
  if (!verificationCode.value || verificationCode.value.length !== 6) {
    verificationError.value = "Please enter a valid 6-digit verification code"
    return
  }

  loading.value = true
  loadingMessage.value = "Verifying..."

  try {
    if (confirmationResult.value) {
      const result = await confirmationResult.value.confirm(
        verificationCode.value
      )
      const user = result.user

      // Check if the user already has a profile in personadata
      const userExists = await personaStore.getUser(user)

      if (userExists) {
        // User exists, proceed with sign-in
        authStore.SetUserDetails({
          uid: user.uid,
          phoneNumber: user.phoneNumber,
          displayName: user.displayName || "",
          isPhoneAuth: true,
        })

        localStorage.setItem("user", JSON.stringify(authStore.currentUser))
        showSnackbar({
          message: "Successfully signed in!",
          type: "success",
        })
        router.push("/property-search")
      } else {
        // User does not exist, redirect to onboarding
        showSnackbar({
          message: "Phone number not registered. Please complete registration.",
          type: "info",
        })
        router.push("/onboarding")
      }
    } else {
      throw new Error("Verification session expired. Please resend the code.")
    }
  } catch (error) {
    console.error("Error verifying code:", error)
    verificationError.value =
      "Invalid verification code or session expired. Please try again."
    showSnackbar({
      message: `Error verifying code: ${error.message}`,
      type: "error",
    })
  } finally {
    loading.value = false
  }
}

const validatePhoneNumber = () => {
  const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/
  if (!phoneNumber.value) {
    phoneError.value = "Phone number is required"
    return false
  } else if (!phoneRegex.test(phoneNumber.value)) {
    phoneError.value = "Please enter a valid US phone number"
    return false
  }
  phoneError.value = ""
  return true
}

const formatPhoneNumber = (number) => {
  const digitsOnly = number.replace(/\D/g, "")
  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`
  } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    return `+${digitsOnly}`
  }
  return `+${digitsOnly}`
}

const formatPhoneNumberForDisplay = (number) => {
  const digitsOnly = number.replace(/\D/g, "")
  if (digitsOnly.length === 10) {
    return `(${digitsOnly.substring(0, 3)}) ${digitsOnly.substring(
      3,
      6
    )}-${digitsOnly.substring(6)}`
  }
  return number
}

const showSnackbar = ({ message, type = "error", duration = 5000 }) => {
  if (snackbar.value.timeout) {
    clearTimeout(snackbar.value.timeout)
  }
  snackbar.value = {
    show: true,
    message,
    type,
    duration,
    timeout: setTimeout(() => {
      closeSnackbar()
    }, duration),
  }
}

const closeSnackbar = () => {
  snackbar.value.show = false
  if (snackbar.value.timeout) {
    clearTimeout(snackbar.value.timeout)
    snackbar.value.timeout = null
  }
}

// Lifecycle hooks
onMounted(() => {
  // Check if user is already authenticated
  // if (authStore.isAuthenticated) {
  //   router.push("/profile-builder")
  // } else {
  //   // Check if we have a stored user
  //   const storedUser = localStorage.getItem("user")
  //   if (storedUser) {
  //     const user = JSON.parse(storedUser)
  //     authStore.SetUserDetails(user)
  //     router.push("/profile-builder")
  //   } else {
  //     console.log("requires login")
  //   }
  // }

  // Initialize reCAPTCHA
  setupRecaptcha()
})

onBeforeUnmount(() => {
  if (snackbar.value.timeout) {
    clearTimeout(snackbar.value.timeout)
  }
})
</script>

<style>
/* Styling for the verification code input */
#verificationCode {
  letter-spacing: 0.5em;
}

/* Animation for tabs */
.tab-enter-active,
.tab-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.tab-enter-from,
.tab-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Animation for background blobs */
@keyframes blob {
  0% {
    transform: translate(0px, 0px) scale(1);
  }

  33% {
    transform: translate(30px, -50px) scale(1.1);
  }

  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }

  100% {
    transform: translate(0px, 0px) scale(1);
  }
}

/* Alternative blob animation */
@keyframes blob-alt {
  0% {
    transform: translate(0px, 0px) scale(1);
  }

  33% {
    transform: translate(-30px, 30px) scale(1.2);
  }

  66% {
    transform: translate(20px, -20px) scale(0.8);
  }

  100% {
    transform: translate(0px, 0px) scale(1);
  }
}

.animate-blob {
  animation: blob 12s infinite cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

.animate-blob-alt {
  animation: blob-alt 10s infinite cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-3000 {
  animation-delay: 3s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}

.animation-delay-5000 {
  animation-delay: 5s;
}

/* Grid pattern background */
.bg-grid-pattern {
  background-image: radial-gradient(circle,
      rgba(255, 255, 255, 0.8) 1px,
      transparent 1px);
  background-size: 30px 30px;
}

/* Floating particles */
.particles {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  overflow: hidden;
  z-index: 0;
}

.particle {
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.7);
  animation: float 15s infinite ease-in-out;
}

.particle-1 {
  top: 20%;
  left: 20%;
  animation-duration: 12s;
  animation-delay: 0s;
}

.particle-2 {
  top: 60%;
  left: 80%;
  width: 8px;
  height: 8px;
  animation-duration: 18s;
  animation-delay: 1s;
}

.particle-3 {
  top: 40%;
  left: 40%;
  width: 5px;
  height: 5px;
  animation-duration: 15s;
  animation-delay: 2s;
}

.particle-4 {
  top: 80%;
  left: 20%;
  width: 7px;
  height: 7px;
  animation-duration: 20s;
  animation-delay: 3s;
}

.particle-5 {
  top: 30%;
  left: 70%;
  width: 4px;
  height: 4px;
  animation-duration: 16s;
  animation-delay: 4s;
}

@keyframes float {

  0%,
  100% {
    transform: translateY(0) translateX(0);
    opacity: 0.7;
  }

  25% {
    transform: translateY(-30px) translateX(20px);
    opacity: 0.3;
  }

  50% {
    transform: translateY(-10px) translateX(40px);
    opacity: 0.8;
  }

  75% {
    transform: translateY(-40px) translateX(-20px);
    opacity: 0.5;
  }
}

/* Animation for the loading spinner */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
